'use client';

import { type FC, type ReactNode, useCallback, useMemo, useState, useTransition } from 'react';
// The progress bar's router: a plain `next/navigation` push shows nothing
// until the new page lands, and a filter click must be answered at once.
import { useRouter } from '@bprogress/next/app';
import { ChevronDown, Search, X } from 'lucide-react';

import { cn } from '@/utils/cn';
import { URLBuilder } from '@/utils/URLBuilder';
import {
  AUCTION_HOUSES,
  AUCTION_HOUSE_LABELS,
  AUCTION_MARKETS,
  AUCTION_MARKET_CURRENCY,
  AUCTION_MARKET_LABELS,
  AUCTION_DATE_WINDOWS,
  getAuctionYearOptions,
  DEFAULT_PRICE_BASIS,
  APIBARA_COLORS,
  APIBARA_DAMAGE_TYPES,
  APIBARA_DRIVE_TYPES,
  APIBARA_FUEL_TYPES,
  APIBARA_KEYS_OPTIONS,
  APIBARA_RUN_CONDITIONS,
  APIBARA_TRANSMISSIONS,
  isVerifiedPriceBasis,
  PRICE_BASIS_OPTIONS,
  type TAuctionHouse,
  type TAuctionMarket,
} from '@/utils/auctions/auctionConfig';
import {
  APIBARA_FILTER_PARAMS,
  type TApibaraFilters,
} from '@/utils/auctions/apibaraFilters';
import {
  FILTER_PARAMS,
  LISTING_MODE_OPTIONS,
  MODE_VALUES,
  toSearchParams,
  type TListingMode,
  type TLotFilters,
  DEFAULT_LOT_SORT,
} from '@/utils/auctions/lotFilters';
import { toAuctionSlug } from '@/utils/auctions/auctionSlugs';

import { Button, SEAM } from '@/components/ui/button/button';
import { Input } from '@/components/ui/input/input';
import { Select } from '@/components/ui/select/select';
import { AsyncSelect } from '@/components/ui/select/async-select';
import { Checkbox } from '@/components/ui/checkbox/checkbox';
import type { TOption } from '@/components/ui/select/types';

import { useAuctionFilterHome } from './auction-filter-home';

/**
 * Filter controls for /aukcje (spec 006, FR-014, FR-015).
 *
 * Built from the shared UI kit (Input / Select / AsyncSelect / Checkbox) so the
 * section looks and behaves like the calculator rather than introducing a second
 * set of form controls.
 *
 * Writes to the URL and lets the Server Component re-fetch — it never talks to
 * the provider itself, which keeps the token server-side (FR-026) and makes every
 * filtered view shareable and back-button friendly.
 *
 * Only filters the provider actually honours are offered, each verified by
 * checking returned rows rather than trusting an HTTP 200. Mileage is deliberately
 * absent: `odometer_from`/`odometer_to` are accepted and then ignored, so a
 * mileage control would look like it worked while returning unfiltered results.
 */

const marketOptions: TOption[] = AUCTION_MARKETS.map(market => ({
  value: market,
  label: `${AUCTION_MARKET_LABELS[market]} — kwoty w ${AUCTION_MARKET_CURRENCY[market]}`,
}));

/**
 * Only the bases with a verified query behind them.
 *
 * There is no estimated market value to filter on: apibara's
 * `pricing.estimated_cost` is a REPAIR cost range, a different quantity, so
 * offering "Wartość rynkowa" would filter on something the visitor was never
 * told about. It is removed rather than re-pointed (FR-011). Buy-now price was
 * never proven to narrow, so it is out on the same rule.
 *
 * The dropdown and `parseLotFilters` read the same allowlist, so a stale
 * `?kwota=wartosc` link falls back to the default basis instead of labelling a
 * chip with a filter that was never applied. Adding a basis to
 * `VERIFIED_PRICE_BASES` restores it in both places at once.
 */
const priceBasisOptions: TOption[] = PRICE_BASIS_OPTIONS.filter(option =>
  isVerifiedPriceBasis(option.value)
).map(option => ({
  value: option.value,
  label: option.label,
}));

const auctionWindowOptions: TOption[] = AUCTION_DATE_WINDOWS.map(option => ({
  value: option.value,
  label: option.label,
}));


/**
 * Years are a select rather than a free number field: it was the only bare input
 * left among the filters, and a typo like "205" or "20155" was accepted by the
 * browser and then silently dropped by the parser — leaving the visitor looking
 * at unfiltered results they believed were filtered.
 */
const yearOptions: TOption[] = getAuctionYearOptions().map(year => ({
  value: String(year),
  label: String(year),
}));

/**
 * Controls for the filters apibara was MEASURED to honour (spec 007, US2).
 *
 * Only these eight. `drive_type` is documented and published but returns zero
 * rows for a value the unfiltered baseline plainly contains, so it is withdrawn
 * rather than shipped as a control that finds nothing — which is precisely the
 * defect this migration exists to fix (FR-002).
 */
const toOptions = (list: readonly { value: string; label: string }[]): TOption[] =>
  list.map(option => ({ value: option.value, label: option.label }));

const transmissionOptions = toOptions(APIBARA_TRANSMISSIONS);
const fuelOptions = toOptions(APIBARA_FUEL_TYPES);
const colorOptions = toOptions(APIBARA_COLORS);
const damageOptions = toOptions(APIBARA_DAMAGE_TYPES);
const driveTypeOptions = toOptions(APIBARA_DRIVE_TYPES);
const runConditionOptions = toOptions(APIBARA_RUN_CONDITIONS);
const keysOptions = toOptions(APIBARA_KEYS_OPTIONS);


const toOption = (value: string | null): TOption | null => (value ? { value, label: value } : null);

const findOption = (options: TOption[], value: string | null): TOption | null =>
  value ? (options.find(option => option.value === value) ?? null) : null;

/**
 * A collapsible filter group.
 *
 * These sections are secondary to make/model/year, and left open they pushed the
 * cars themselves below the fold — backwards on a listings page. Collapsing them
 * is only safe with two guarantees, both implemented by the caller:
 *  - a section holding an active filter starts OPEN, so a shared link never
 *    arrives with its own filters hidden;
 *  - the header carries a count, so "something is set in here" is legible while
 *    closed.
 *
 * Content is unmounted when closed. Field values live in React state rather than
 * the DOM, so nothing is lost and a collapsed section still submits.
 */
/** The middle third of a three-way seam pair: cut on both sides, no radius. */
const SEAM_MIDDLE =
  '[clip-path:polygon(18px_0,100%_0,calc(100%-18px)_100%,0_100%)] -ml-4 rounded-none pl-8 pr-8 z-[5] hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.35)]';

const CollapsibleSection: FC<{
  id: string;
  title: string;
  activeCount: number;
  isOpen: boolean;
  onToggle: () => void;
  children: ReactNode;
}> = ({ id, title, activeCount, isOpen, onToggle, children }) => (
  <div className="flex flex-col gap-4 border-t border-neutral-800 pt-4">
    <h2>
      <button
        type="button"
        id={`${id}-trigger`}
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-controls={id}
        className="group flex w-full items-center gap-3 text-left"
      >
        <span className="text-xs font-semibold uppercase tracking-[0.14em] text-foreground/40 transition-colors group-hover:text-foreground/70">
          {title}
        </span>

        {activeCount > 0 && (
          <span className="rounded-full bg-action/15 px-2 py-0.5 text-xs font-semibold text-action">
            {activeCount}
            <span className="sr-only"> aktywnych filtrów w tej sekcji</span>
          </span>
        )}

        <span className="ml-auto text-foreground/40 transition-colors group-hover:text-foreground/70">
          <ChevronDown
            size={16}
            aria-hidden
            className={cn('transition-transform duration-200', isOpen && 'rotate-180')}
          />
        </span>
      </button>
    </h2>

    {isOpen && (
      <div
        id={id}
        role="region"
        aria-labelledby={`${id}-trigger`}
        className="flex flex-col gap-4"
      >
        {children}
      </div>
    )}
  </div>
);

/**
 * A checkbox in a bordered tile that lights up when selected. Four bare
 * checkboxes on one line read as a form; tiles read as choices, and the selected
 * state is visible at a glance from across the page.
 */
const CheckboxTile: FC<{
  id: string;
  label: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
}> = ({ id, label, checked, disabled = false, onChange }) => (
  <div
    className={cn(
      'rounded-xl border px-4 py-3 transition-colors duration-150',
      checked
        ? 'border-action/60 bg-action/[0.06]'
        : 'border-foreground/20 bg-backgroundLight/40 hover:border-action/40',
      disabled && 'opacity-60'
    )}
  >
    <Checkbox id={id} label={label} defaultChecked={checked} disabled={disabled} onChange={onChange} />
  </div>
);

type TAuctionLotFilters = {
  /**
   * apibara-only filters read back from the URL, so a shared link restores the
   * controls it was built with and not just the result set (FR-019).
   */
  extras?: Omit<TApibaraFilters, keyof TLotFilters>;
  filters: TLotFilters;
  /** After Szukaj or Wyczyść — the drawer closes on it. */
  onSubmitted?: () => void;
};

export const AuctionLotFilters: FC<TAuctionLotFilters> = ({
  filters,
  extras,
  onSubmitted,
}) => {
  const router = useRouter();
  const home = useAuctionFilterHome();
  const [isPending, startTransition] = useTransition();

  const [make, setMake] = useState<TOption | null>(toOption(filters.make));
  const [model, setModel] = useState<TOption | null>(toOption(filters.model));
  const [yearFrom, setYearFrom] = useState(filters.yearFrom ? String(filters.yearFrom) : '');
  const [yearTo, setYearTo] = useState(filters.yearTo ? String(filters.yearTo) : '');
  const [market, setMarket] = useState<TOption | null>(
    findOption(marketOptions, filters.markets[0] ?? null)
  );
  const [houses, setHouses] = useState<TAuctionHouse[]>(filters.houses);
  const [priceBasis, setPriceBasis] = useState<TOption>(
    findOption(priceBasisOptions, filters.priceBasis) ?? priceBasisOptions[0]
  );
  const [priceFrom, setPriceFrom] = useState(filters.priceFrom !== null ? String(filters.priceFrom) : '');
  const [priceTo, setPriceTo] = useState(filters.priceTo !== null ? String(filters.priceTo) : '');
  const [buyNowOnly, setBuyNowOnly] = useState(filters.buyNowOnly);
  // Which auctions the listing shows (spec 015): upcoming, running now, or sold.
  const [mode, setMode] = useState<TListingMode>(filters.mode);
  // A lot number or VIN: when set, it is the whole query (spec 015 follow-up).
  const [lookup, setLookup] = useState(filters.lookup ?? '');

  // apibara-only filters. Held regardless of provider so switching does not
  // discard a visitor's selection mid-session; they are only RENDERED and only
  // submitted when that provider is serving the page.
  const [transmission, setTransmission] = useState<TOption | null>(
    findOption(transmissionOptions, extras?.transmission[0] ?? null)
  );
  const [fuel, setFuel] = useState<TOption | null>(findOption(fuelOptions, extras?.fuel[0] ?? null));
  const [color, setColor] = useState<TOption | null>(
    findOption(colorOptions, extras?.color[0] ?? null)
  );
  const [damage, setDamage] = useState<TOption | null>(
    findOption(damageOptions, extras?.damage[0] ?? null)
  );
  const [driveType, setDriveType] = useState<TOption | null>(
    findOption(driveTypeOptions, extras?.driveType[0] ?? null)
  );
  const [runCondition, setRunCondition] = useState<TOption | null>(
    findOption(runConditionOptions, extras?.runCondition ?? null)
  );
  const [keys, setKeys] = useState<TOption | null>(findOption(keysOptions, extras?.keys ?? null));
  const [odometerFrom, setOdometerFrom] = useState(
    extras?.odometerFrom !== null && extras?.odometerFrom !== undefined
      ? String(extras.odometerFrom)
      : ''
  );
  const [odometerTo, setOdometerTo] = useState(
    extras?.odometerTo !== null && extras?.odometerTo !== undefined ? String(extras.odometerTo) : ''
  );
  // Open when it already carries a selection, so a shared link does not hide
  // the filters that produced the result set.
  const specApplied = Boolean(
    extras &&
      (extras.transmission.length ||
        extras.fuel.length ||
        extras.color.length ||
        extras.damage.length ||
        extras.driveType.length ||
        extras.runCondition ||
        extras.keys ||
        extras.odometerFrom !== null ||
        extras.odometerTo !== null)
  );
  const [isSpecOpen, setIsSpecOpen] = useState(specApplied);


  const specSectionCount =
    [transmission, fuel, color, damage, driveType, runCondition, keys].filter(Boolean).length +
    (odometerFrom || odometerTo ? 1 : 0);
  const [auctionWindow, setAuctionWindow] = useState<TOption | null>(
    findOption(auctionWindowOptions, filters.auctionWindow)
  );

  /**
   * Seeded from the APPLIED filters, not form state: a section that already
   * carries a filter must not start collapsed, or a shared link would hide the
   * very thing it was shared to show. `useState` initialisers run once, so later
   * edits toggle the panel only when the visitor asks.
   */
  const [isAmountOpen, setIsAmountOpen] = useState(
    () => filters.priceFrom !== null || filters.priceTo !== null || filters.markets.length > 0
  );
  const [isAuctionOpen, setIsAuctionOpen] = useState(
    () =>
      Boolean(filters.auctionWindow) ||
      filters.buyNowOnly ||
      filters.houses.length > 0
  );

  /**
   * THE FORM FOLLOWS THE APPLIED QUERY.
   *
   * Every field above is seeded from `filters` by a `useState` initialiser,
   * and those run ONCE. The form is never remounted (it has two homes and must
   * survive a resize), so when a navigation this form did not make changed what
   * the page shows — Back, the breadcrumb, a chip removed, a link into a
   * filtered view — the controls kept the old query and contradicted the list
   * beside them: `/aukcje` rendered upcoming auctions with "Sprzedane" still
   * the pressed mode (owner, 2026-09-15).
   *
   * Reseeding DURING RENDER is React's own answer to "a prop changed, reset the
   * state" and, unlike a `key` remount, it keeps the DOM — so focus stays on
   * the control that was just used and the drawer does not blink.
   *
   * Nothing a visitor typed is lost: `apply` submits the WHOLE form, so after a
   * filter the applied query IS the form and every setter below is a no-op.
   * Only a navigation the form did not make resets it, which is the navigation
   * the visitor asked for.
   */
  const appliedQuery = `${toSearchParams(filters).toString()}|${JSON.stringify(extras ?? null)}`;
  const [seededFrom, setSeededFrom] = useState(appliedQuery);

  if (seededFrom !== appliedQuery) {
    setSeededFrom(appliedQuery);
    setMode(filters.mode);
    setLookup(filters.lookup ?? '');
    setMake(toOption(filters.make));
    setModel(toOption(filters.model));
    setYearFrom(filters.yearFrom ? String(filters.yearFrom) : '');
    setYearTo(filters.yearTo ? String(filters.yearTo) : '');
    setMarket(findOption(marketOptions, filters.markets[0] ?? null));
    setHouses(filters.houses);
    setPriceBasis(findOption(priceBasisOptions, filters.priceBasis) ?? priceBasisOptions[0]);
    setPriceFrom(filters.priceFrom !== null ? String(filters.priceFrom) : '');
    setPriceTo(filters.priceTo !== null ? String(filters.priceTo) : '');
    setBuyNowOnly(filters.buyNowOnly);
    setAuctionWindow(findOption(auctionWindowOptions, filters.auctionWindow));
    setTransmission(findOption(transmissionOptions, extras?.transmission[0] ?? null));
    setFuel(findOption(fuelOptions, extras?.fuel[0] ?? null));
    setColor(findOption(colorOptions, extras?.color[0] ?? null));
    setDamage(findOption(damageOptions, extras?.damage[0] ?? null));
    setDriveType(findOption(driveTypeOptions, extras?.driveType[0] ?? null));
    setRunCondition(findOption(runConditionOptions, extras?.runCondition ?? null));
    setKeys(findOption(keysOptions, extras?.keys ?? null));
    setOdometerFrom(extras?.odometerFrom !== null && extras?.odometerFrom !== undefined ? String(extras.odometerFrom) : '');
    setOdometerTo(extras?.odometerTo !== null && extras?.odometerTo !== undefined ? String(extras.odometerTo) : '');

    // Disclosure, not value: a section the new query fills is opened, but one
    // the visitor opened themselves is never closed under them.
    if (filters.priceFrom !== null || filters.priceTo !== null || filters.markets.length) setIsAmountOpen(true);
    if (filters.auctionWindow || filters.buyNowOnly || filters.houses.length) setIsAuctionOpen(true);
    if (specApplied) setIsSpecOpen(true);
  }


  const hasPriceRange = Boolean(priceFrom || priceTo);

  /**
   * Filtering on the buy-now PRICE already restricts the set to buy-now lots, so
   * the checkbox does nothing in that combination — measured, not assumed:
   * `buy_now_price` 5,000–6,000 returns 2,631 lots with or without `is_buy_now`.
   * It stays visible but locked, because a control that silently does nothing is
   * worse than one that explains why it is already satisfied.
   *
   * Everywhere else it earns its place: against the default live-bid basis the
   * same range drops 1,645 lots to 368, and it is the only way to ask for all
   * 44,564 buy-now cars without inventing a price band.
   *
   * DORMANT while `oferta` is the only verified basis — `kupTeraz` cannot be
   * selected or parsed today, so this reads false. It comes back with the basis,
   * which is why the measurement above is kept rather than deleted.
   */
  const buyNowImpliedByPrice = priceBasis.value === 'kupTeraz' && hasPriceRange;

  /**
   * Badges count what each section currently holds, read from form state so they
   * update as the visitor edits. The chip row below the form is the separate,
   * URL-derived record of what is actually applied.
   */
  const amountSectionCount = (hasPriceRange ? 1 : 0) + (market ? 1 : 0);
  const auctionSectionCount =
    (auctionWindow ? 1 : 0) +
    (buyNowOnly || buyNowImpliedByPrice ? 1 : 0) +
    houses.length;

  const selectedMarket = (market?.value as TAuctionMarket | undefined) ?? null;
  const currency = selectedMarket ? AUCTION_MARKET_CURRENCY[selectedMarket] : null;
  const priceSuffix = currency ? ` (${currency})` : '';

  const isFiltered = Boolean(
    lookup.trim() ||
    mode !== 'live' ||
    make ||
      model ||
      yearFrom ||
      yearTo ||
      market ||
      houses.length ||
      hasPriceRange ||
      buyNowOnly ||
      auctionWindow
  );

  /**
   * Suggestions come from our own route, never the provider directly — the API
   * token must stay server-side, and the raw catalogues (2,849 makes, 10,039
   * models) are far too large to ship to the browser.
   */
  const loadDictionary = useCallback(
    async (
      type: 'makes' | 'models',
      query: string,
      /**
       * The make's NAME. There is no numeric make id — the name IS the
       * identifier, and the `id` our own route returns for a make is only an
       * array index for react-select's list key.
       *
       * An earlier version sent that index as `makeId` and nothing else, so the
       * route looked up `modelsByMake['']` and every model dropdown answered
       * "Brak modeli", for every make. There is now one way to scope a model
       * lookup, which is what stops the two drifting apart again.
       */
      forMakeName?: string | null
    ): Promise<TOption[]> => {
      if (type === 'models' && !forMakeName) return [];

      const params = new URLSearchParams({ type, q: query });

      if (forMakeName) params.set('make', forMakeName);

      try {
        const response = await fetch(`/api/aukcje/slowniki?${params}`);
        const data = await response.json();

        return (data.items ?? []).map((item: { id: number; name: string }) => ({
          value: item.name,
          label: item.name,
          id: item.id,
        }));
      } catch {
        // Autocomplete is an assist, not the feature — degrade to no suggestions.
        return [];
      }
    },
    []
  );

  const loadMakes = useCallback((query: string) => loadDictionary('makes', query), [loadDictionary]);

  /*
   * A shared link carries the make as a name (`?marka=BMW`), and the model
   * lookup is scoped by that same name — so the `make` state seeded above is all
   * that is needed. An earlier version had to fetch a numeric id here before the
   * model field would enable at all; with the name as the identifier, that round
   * trip is gone and a shared URL works on first paint.
   */

  const loadModels = useCallback(
    (query: string) => loadDictionary('models', query, make ? String(make.value) : filters.make),
    [loadDictionary, make, filters.make]
  );

  const handleMakeChange = (option: TOption | null) => {
    setMake(option);
    // The model list is scoped to the make, so changing it invalidates the model.
    setModel(null);
  };

  /**
   * Narrowing "do" to years at or after "od" keeps the pair coherent while it is
   * being built. If the new floor invalidates the ceiling already chosen, the
   * ceiling is cleared rather than silently left contradicting it.
   */
  const yearToOptions = useMemo(
    () => (yearFrom ? yearOptions.filter(option => Number(option.value) >= Number(yearFrom)) : yearOptions),
    [yearFrom]
  );

  const handleYearFromChange = (option: TOption | null) => {
    const value = option ? String(option.value) : '';

    setYearFrom(value);

    if (value && yearTo && Number(yearTo) < Number(value)) setYearTo('');
  };

  const toggleHouse = (house: TAuctionHouse, checked: boolean) =>
    setHouses(current => (checked ? [...current, house] : current.filter(entry => entry !== house)));

  // `nextMode` lets the mode control apply on click, before React has
  // re-rendered with the new state (owner feedback, 2026-09-07).
  const apply = (nextMode: TListingMode = mode) => {
    // A lot number or VIN is a direct lookup: it goes alone, to the root, and
    // every other control is left as it is for when the field is cleared.
    const wanted = lookup.trim().toUpperCase();

    if (wanted) {
      const only = new URLSearchParams();
      only.set(FILTER_PARAMS.lookup, wanted);
      startTransition(() => {
        router.push(URLBuilder.paths.getAuctionLots(1, only, null, null));
        onSubmitted?.();
      });

      return;
    }

    const params = new URLSearchParams();

    if (yearFrom) params.set(FILTER_PARAMS.yearFrom, yearFrom);
    if (yearTo) params.set(FILTER_PARAMS.yearTo, yearTo);
    if (priceFrom) params.set(FILTER_PARAMS.priceFrom, priceFrom);
    if (priceTo) params.set(FILTER_PARAMS.priceTo, priceTo);

    // Inert without a bound — see toSearchParams.
    if (hasPriceRange && priceBasis.value !== DEFAULT_PRICE_BASIS) {
      params.set(FILTER_PARAMS.priceBasis, String(priceBasis.value));
    }

    const p = APIBARA_FILTER_PARAMS;

    if (transmission) params.set(p.transmission, String(transmission.value));
    if (fuel) params.set(p.fuel, String(fuel.value));
    if (color) params.set(p.color, String(color.value));
    if (damage) params.set(p.damage, String(damage.value));
    if (driveType) params.set(p.driveType, String(driveType.value));
    if (runCondition) params.set(p.runCondition, String(runCondition.value));
    if (keys) params.set(p.keys, String(keys.value));
    if (odometerFrom) params.set(p.odometerFrom, odometerFrom);
    if (odometerTo) params.set(p.odometerTo, odometerTo);

    // The mode rides along (spec 015); the auction window and buy-now only mean
    // something for upcoming auctions, so they are written in live mode only.
    if (nextMode !== 'live') params.set(FILTER_PARAMS.mode, MODE_VALUES[nextMode]);
    // The order lives above the list (spec 017); re-filtering keeps it.
    if (filters.sort !== DEFAULT_LOT_SORT) params.set(FILTER_PARAMS.sort, filters.sort);
    if (auctionWindow && nextMode === 'live') params.set(FILTER_PARAMS.auctionWindow, String(auctionWindow.value));
    if (buyNowOnly && nextMode === 'live') params.set(FILTER_PARAMS.buyNowOnly, '1');
    if (selectedMarket) params.set(FILTER_PARAMS.market, selectedMarket);

    houses.forEach(house => params.append(FILTER_PARAMS.house, house));

    // Make and model go in the PATH, not the query (FR-027), so the form
    // produces the same canonical URL the site links to everywhere else.
    const makeSlug = make ? toAuctionSlug(String(make.value)) : null;
    const modelSlug = makeSlug && model ? toAuctionSlug(String(model.value)) : null;

    // Filtering always returns to page 1 — staying on page 9 of a different
    // result set would land the visitor on an empty page.
    startTransition(() => {
      router.push(URLBuilder.paths.getAuctionLots(1, params, makeSlug, modelSlug));
    });
    home?.close();
    onSubmitted?.();
  };

  const clear = () => {
    setLookup('');
    setMode('live');
    setMake(null);
    setModel(null);
    setYearFrom('');
    setYearTo('');
    setMarket(null);
    setHouses([]);
    setPriceBasis(priceBasisOptions[0]);
    setTransmission(null);
    setFuel(null);
    setColor(null);
    setDamage(null);
    setDriveType(null);
    setRunCondition(null);
    setKeys(null);
    setOdometerFrom('');
    setOdometerTo('');
    setPriceFrom('');
    setPriceTo('');
    setBuyNowOnly(false);
    setAuctionWindow(null);
    setIsAmountOpen(false);
    setIsAuctionOpen(false);
    startTransition(() => {
      router.push(URLBuilder.paths.getAuctionLots());
    });
    home?.close();
    onSubmitted?.();
  };

  return (
    <form
        // `relative z-20` keeps the open dropdowns above the results below.
        // AnimatedListItem wraps each lot card in a motion div, and its transform
        // creates a stacking context — so the cards paint above this form purely by
        // DOM order, and raising the menu's own z-index inside the form would not
        // help. Positioning the form lifts the whole subtree, menus included.
        className="relative z-20 flex flex-col gap-5 rounded-2xl border border-neutral-800 bg-backgroundLight/40 p-5"
        onSubmit={event => {
          event.preventDefault();
          apply();
        }}
      >
        {/* Which auctions the list shows — first, because it changes what every
            other control means. Three halves joined along the seam; the middle
            one is cut on both sides. */}
        <div className="flex flex-col gap-3">
          <h2 id="auction-mode-label" className="text-xs font-semibold uppercase tracking-[0.14em] text-foreground/40">
            Rodzaj aukcji
          </h2>
          <div role="group" aria-labelledby="auction-mode-label" data-mode-control="" className="flex items-stretch">
            {LISTING_MODE_OPTIONS.map((option, index) => {
              const isSelected = option.value === mode;
              const shape =
                index === 0
                  ? SEAM.label.prev
                  : index === LISTING_MODE_OPTIONS.length - 1
                    ? SEAM.label.next
                    : SEAM_MIDDLE;

              return (
                <Button
                  key={option.value}
                  type="button"
                  size="sm"
                  variant={isSelected ? 'primary' : 'secondary'}
                  aria-pressed={isSelected}
                  onClick={() => {
                    setMode(option.value);
                    apply(option.value);
                  }}
                  className={cn('flex-1 px-2', shape)}
                >
                  {option.label}
                </Button>
              );
            })}
          </div>
        </div>

        <div className="flex flex-col gap-4">
          <h2 className="text-xs font-semibold uppercase tracking-[0.14em] text-foreground/40">
            Pojazd
          </h2>

          {/* A narrow column now (sidebar or drawer): make and model take the
              full width so long marques ("MERCEDES-BENZ") are never truncated,
              and only the 4-digit years share a row. */}
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 flex flex-col gap-1.5">
              <Input
                id={FILTER_PARAMS.lookup}
                label="Numer lotu lub VIN"
                value={lookup}
                onChange={setLookup}
                placeholder="np. 45690732 lub 1HGCM82633A123456"
              />
              <p className="pl-1 text-xs text-foreground/50">Pełny VIN (17 znaków) albo numer lotu. Szuka bezpośrednio — pozostałe filtry są wtedy pomijane.</p>
            </div>

            <AsyncSelect
              menuPlacement="auto"
              className="col-span-2"
              id="auction-make"
              label="Marka"
              value={make}
              onChange={handleMakeChange}
              loadOptions={loadMakes}
              placeholder="np. BMW"
            />

            <AsyncSelect
              menuPlacement="auto"
              // Remounting on make change is load-bearing, not a hack: react-select
              // resolves `defaultOptions` once on mount, so without a new key the
              // model list would stay empty until the visitor typed. It also clears
              // `cacheOptions`, which would otherwise serve the previous make's
              // models for the same query.
              key={make ? String(make.value) : 'no-make'}
              className="col-span-2"
              id="auction-model"
              label="Model"
              value={model}
              onChange={setModel}
              loadOptions={loadModels}
              // The provider scopes models to a make, so the field stays disabled
              // until one is picked from the list.
              isDisabled={!make}
              defaultOptions={make ? true : []}
              placeholder={make ? 'np. X5' : 'Najpierw wybierz markę'}
              noOptionsMessage={make ? 'Brak modeli' : 'Najpierw wybierz markę'}
            />

            <Select
              menuPlacement="auto"
              id={FILTER_PARAMS.yearFrom}
              label="Rocznik od"
              options={yearOptions}
              value={findOption(yearOptions, yearFrom || null)}
              onChange={handleYearFromChange}
              placeholder="Dowolny"
            />
            <Select
              menuPlacement="auto"
              id={FILTER_PARAMS.yearTo}
              label="Rocznik do"
              // Only years at or after "od" — an inverted range is never a real
              // intent. The parser still swaps inverted pairs, which now guards
              // hand-edited URLs rather than the UI.
              options={yearToOptions}
              value={findOption(yearOptions, yearTo || null)}
              onChange={option => setYearTo(option ? String(option.value) : '')}
              placeholder="Dowolny"
            />
          </div>
        </div>

        <CollapsibleSection
          id="auction-filters-amount"
          title="Kwota i rynek"
          activeCount={amountSectionCount}
          isOpen={isAmountOpen}
          onToggle={() => setIsAmountOpen(open => !open)}
        >
          <div className="grid grid-cols-2 gap-4">
            <Select
              menuPlacement="auto"
              className="col-span-2"
              id="auction-price-basis"
              label="Filtruj kwotę"
              options={priceBasisOptions}
              value={priceBasis}
              // Clearing the select must not leave the basis undefined — the range
              // has to apply to something, so it falls back to the default.
              onChange={option => setPriceBasis(option ?? priceBasisOptions[0])}
              placeholder="Aktualna oferta"
            />

            <Select
              menuPlacement="auto"
              className="col-span-2"
              id="auction-market"
              label="Rynek"
              options={marketOptions}
              value={market}
              onChange={setMarket}
              placeholder="USA i Kanada"
            />

            <Input
              id={FILTER_PARAMS.priceFrom}
              label={`Kwota od${priceSuffix}`}
              type="number"
              value={priceFrom}
              onChange={setPriceFrom}
              placeholder="1000"
            />
            <Input
              id={FILTER_PARAMS.priceTo}
              label={`Kwota do${priceSuffix}`}
              type="number"
              value={priceTo}
              onChange={setPriceTo}
              placeholder="20000"
            />
          </div>

          {/* The currency question is the whole reason "Rynek" sits in this
              section: without a market, a range spans two currencies that the
              provider compares as bare numbers and never converts. Saying so is
              the only honest option (constitution Principle I). */}
          {currency ? (
            <p className="pl-1 text-xs text-foreground/50">
              Kwoty w {currency} — {AUCTION_MARKET_LABELS[selectedMarket as TAuctionMarket]}.
            </p>
          ) : hasPriceRange ? (
            <p className="rounded-lg border border-action/40 bg-action/[0.06] px-3 py-2 text-xs text-foreground/80">
              Szukasz na dwóch rynkach naraz, więc kwoty są porównywane w dwóch walutach (USD i CAD)
              bez przeliczania. Wybierz rynek, aby filtrować w jednej walucie.
            </p>
          ) : (
            <p className="pl-1 text-xs text-foreground/50">
              Bez wybranego rynku kwoty są w walucie danej aukcji (USD lub CAD) i nie są przeliczane.
            </p>
          )}
        </CollapsibleSection>

        {/* Only rendered under apibara. On the incumbent every one of these
            parameters is accepted with HTTP 200 and then ignored, so the section
            would be eight controls that quietly do nothing (FR-002). */}
        {(
          <CollapsibleSection
            id="auction-filters-spec"
            title="Stan i wyposażenie"
            activeCount={specSectionCount}
            isOpen={isSpecOpen}
            onToggle={() => setIsSpecOpen(open => !open)}
          >
            <div className="grid grid-cols-1 gap-4">
              <Select
                menuPlacement="auto"
                id="auction-run-condition"
                label="Stan techniczny"
                options={runConditionOptions}
                value={runCondition}
                onChange={setRunCondition}
                placeholder="Dowolny"
              />
              <Select
                menuPlacement="auto"
                id="auction-damage"
                label="Rodzaj szkody"
                options={damageOptions}
                value={damage}
                onChange={setDamage}
                placeholder="Dowolna"
              />
              <Select
                menuPlacement="auto"
                id="auction-drive-type"
                label="Napęd"
                options={driveTypeOptions}
                value={driveType}
                onChange={setDriveType}
                placeholder="Dowolny"
              />
              <Select
                menuPlacement="auto"
                id="auction-transmission"
                label="Skrzynia biegów"
                options={transmissionOptions}
                value={transmission}
                onChange={setTransmission}
                placeholder="Dowolna"
              />
              <Select
                menuPlacement="auto"
                id="auction-fuel"
                label="Paliwo"
                options={fuelOptions}
                value={fuel}
                onChange={setFuel}
                placeholder="Dowolne"
              />
              <Select
                menuPlacement="auto"
                id="auction-color"
                label="Kolor"
                options={colorOptions}
                value={color}
                onChange={setColor}
                placeholder="Dowolny"
              />
              <Select
                menuPlacement="auto"
                id="auction-keys"
                label="Kluczyki"
                options={keysOptions}
                value={keys}
                onChange={setKeys}
                placeholder="Bez znaczenia"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Input
                id={APIBARA_FILTER_PARAMS.odometerFrom}
                label="Przebieg od (mile)"
                type="number"
                value={odometerFrom}
                onChange={setOdometerFrom}
                placeholder="0"
              />
              <Input
                id={APIBARA_FILTER_PARAMS.odometerTo}
                label="Przebieg do (mile)"
                type="number"
                value={odometerTo}
                onChange={setOdometerTo}
                placeholder="150000"
              />
            </div>

            {/* Miles, not kilometres — both auction houses report in miles and
                converting here would invite a rounded number being read as exact. */}
            <p className="pl-1 text-xs text-foreground/50">
              Przebieg podajemy w milach, tak jak raportują go domy aukcyjne.
            </p>
          </CollapsibleSection>
        )}

        <CollapsibleSection
          id="auction-filters-auction"
          title="Aukcja"
          activeCount={auctionSectionCount}
          isOpen={isAuctionOpen}
          onToggle={() => setIsAuctionOpen(open => !open)}
        >
          <div className="grid grid-cols-1 gap-4">

            <div className="flex flex-col gap-2" role="group" aria-labelledby="auction-house-label">
              <p
                id="auction-house-label"
                className="pl-1 text-sm font-semibold tracking-wider text-foreground"
              >
                Dom aukcyjny
              </p>
              <div className="grid grid-cols-2 gap-3">
                {AUCTION_HOUSES.map(house => (
                  <CheckboxTile
                    key={house}
                    id={`${FILTER_PARAMS.house}-${house}`}
                    label={AUCTION_HOUSE_LABELS[house]}
                    checked={houses.includes(house)}
                    onChange={checked => toggleHouse(house, checked)}
                  />
                ))}
              </div>
            </div>
          </div>

          {mode === 'live' && (
          <>
          <div className="grid grid-cols-1 gap-4">
            <div className="flex flex-col gap-1.5">
              <Select
                menuPlacement="auto"
                id="auction-window"
                label="Termin aukcji"
                options={auctionWindowOptions}
                value={auctionWindow}
                onChange={setAuctionWindow}
                placeholder="Dowolny"
              />
              {/* Weekends are empty at both houses, so the presets are whole
                  windows from today rather than a day picker that can land on a
                  guaranteed-empty Saturday. */}
              <p className="pl-1 text-xs text-foreground/50">Licząc od dziś.</p>
            </div>

          </div>

          <div className="flex flex-col gap-1.5">
            <CheckboxTile
              id={FILTER_PARAMS.buyNowOnly}
              label="Tylko z opcją „Kup teraz”"
              checked={buyNowOnly || buyNowImpliedByPrice}
              disabled={buyNowImpliedByPrice}
              onChange={setBuyNowOnly}
            />
            {buyNowImpliedByPrice && (
              <p className="pl-1 text-xs text-foreground/50">
                Wynika już z filtra ceny „Kup teraz”.
              </p>
            )}
          </div>
          </>
          )}
        </CollapsibleSection>

        {/* One joined pair, cut by the seam the pagination and the photo tags
            share: Wyczyść on the left, Szukaj — the primary action — on the
            right, where the pair ends. Both halves are ALWAYS rendered — the
            clear half goes dark when there is nothing to clear — so picking a
            first option never grows the form and pushes a new button below
            the drawer's fold. */}
        <div className="flex items-stretch border-t border-neutral-800 pt-5">
          <Button
            type="button"
            variant="secondary"
            onClick={clear}
            disabled={isPending || !isFiltered}
            startIcon={<X size={16} aria-hidden />}
            className={cn('flex-1', SEAM.label.prev, !isFiltered && SEAM.off)}
          >
            Wyczyść<span className="sr-only"> filtry</span>
          </Button>

          <Button
            type="submit"
            loading={isPending}
            startIcon={<Search size={16} aria-hidden />}
            className={cn('flex-1', SEAM.label.next)}
          >
            {isPending ? 'Szukam…' : 'Szukaj'}
          </Button>
        </div>
      </form>
  );
};
